import {fetchAuthSession} from "aws-amplify/auth";
import {Amplify} from "aws-amplify";

export type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

export class ApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public code?: string,
    public details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export type ApiRequestParams = {
  path: string;
  method?: HttpMethod;
  body?: unknown;
  isPublic?: boolean;
};

export async function apiRequest<T>(params: ApiRequestParams): Promise<T> {
  const method = params.method ?? "GET";

  const cfg = Amplify.getConfig() as { API?: { REST?: Record<string, { endpoint?: string }> } };
  const rest = cfg?.API?.REST;
  // This app is an admin client only: every authenticated call goes to the Admin API. There is
  // deliberately no core-API fallback, so a core-API call cannot be expressed here at all.
  const baseUrl = params.isPublic
    ? rest?.CalendallSignUpApi?.endpoint
    : rest?.CalendallAdminApi?.endpoint;

  if (!baseUrl) {
    throw new ApiError(
      "Amplify API endpoint is not configured. Provide /assets/aws-exports.json (for local dev: public/assets/aws-exports.json).",
      500,
      "CONFIGURATION_ERROR",
    );
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (!params.isPublic) {
    try {
      const session = await fetchAuthSession();
      const token = session?.tokens?.idToken?.toString() ?? "";
      if (!token) {
        throw new ApiError("Authentication required", 401, "AUTHENTICATION_ERROR");
      }
      headers.Authorization = `Bearer ${token}`;
    } catch (error) {
      throw new ApiError("Failed to authenticate", 401, "AUTHENTICATION_ERROR", error);
    }
  }

  const url = `${baseUrl}/${params.path}`.replace(/\/+/g, "/").replace(":/", "://");

  try {
    const res = await fetch(url, {
      method,
      headers,
      body: params.body === undefined ? undefined : JSON.stringify(params.body),
    });

    if (!res.ok) {
      let errorDetails: unknown;
      let errorMessage = `HTTP ${res.status}`;

      try {
        const text = await res.text();
        if (text) {
          try {
            errorDetails = JSON.parse(text);
            errorMessage =
              typeof errorDetails === "object" && errorDetails !== null && "message" in errorDetails
                ? (errorDetails as { message: string }).message
                : text;
          } catch {
            errorMessage = text;
          }
        }
      } catch {
        // If we can't read the response body, use the status text
        errorMessage = res.statusText || `HTTP ${res.status}`;
      }

      // Map common HTTP status codes to user-friendly messages
      const statusMessages: Record<number, string> = {
        400: "Bad request - Please check your input",
        401: "Authentication required - Please log in again",
        403: "Access denied - You don't have permission to perform this action",
        404: "Resource not found",
        409: "Conflict - The resource already exists",
        422: "Validation error - Please check your input",
        429: "Too many requests - Please try again later",
        500: "Server error - Please try again later",
        502: "Service unavailable - Please try again later",
        503: "Service unavailable - Please try again later",
      };

      const userMessage = statusMessages[res.status] || errorMessage;

      throw new ApiError(userMessage, res.status, res.status.toString(), errorDetails);
    }

    if (res.status === 204) return undefined as T;
    return (await res.json()) as T;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle network errors, timeouts, etc.
    if (error instanceof Error) {
      if (error.name === "TypeError" && error.message.includes("fetch")) {
        throw new ApiError("Network error - Please check your connection", 0, "NETWORK_ERROR", error);
      }
      if (error.name === "AbortError") {
        throw new ApiError("Request timeout - Please try again", 0, "TIMEOUT_ERROR", error);
      }
    }

    throw new ApiError("An unexpected error occurred", 0, "UNKNOWN_ERROR", error);
  }
}
